import { useEffect, useState } from "react";

/**
 * PWA install prompt — mobile only.
 *
 * Why this exists: Chrome on Android only shows the install mini-infobar
 * inconsistently (deprecated in 2018, replaced by an icon in the address
 * bar most users never notice). Safari on iOS has never supported the
 * beforeinstallprompt event at all — the only path is the Share menu.
 * So we have to surface installability ourselves on both platforms.
 *
 * Android: captures the `beforeinstallprompt` event the moment the
 *   browser fires it (PWA install criteria met) and offers a button
 *   that calls prompt() on it.
 *
 * iOS Safari: shows an instructional banner with the Share-then-Add-to-
 *   Home-Screen steps, since there is no programmatic install API.
 *
 * Both: dismiss persists in localStorage so we do not pester. Hidden in
 *   the standalone (already-installed) display mode.
 */

const DISMISS_KEY = "ariv_install_prompt_dismissed_v1";
const DISMISS_TTL_DAYS = 30; // re-show after a month if they dismissed without installing

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac — also detect via touch.
  const iPadOS = ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document;
  return /iPhone|iPad|iPod/i.test(ua) || iPadOS;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari exposes navigator.standalone
  return !!window.navigator.standalone;
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / 86400000;
    return ageDays < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore (private mode, full storage, etc.)
  }
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosBanner, setIosBanner] = useState(false);

  useEffect(() => {
    if (!isMobile()) return;          // desktop already has the address-bar icon
    if (isStandalone()) return;       // already installed
    if (isDismissed()) return;        // recently dismissed

    // Android / Chromium path — capture the deferred prompt.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Hide if the install completes (user accepted, OS confirmed).
    const onInstalled = () => {
      setShow(false);
      setIosBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS path — no event ever fires, so trigger the banner after a
    // short delay so it does not blast on initial paint.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setIosBanner(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "dismissed") {
        markDismissed();
      }
    } catch {
      // ignore — user closed, browser refused, etc.
    } finally {
      setDeferredPrompt(null);
      setShow(false);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setShow(false);
    setIosBanner(false);
  };

  // Android banner — only renders once a deferred prompt is captured.
  if (show && deferredPrompt) {
    return (
      <div
        role="dialog"
        aria-label="Install Ariv app"
        className="fixed left-3 right-3 bottom-3 z-50 bg-white border border-gray-200 shadow-xl rounded-2xl p-4 flex items-center gap-3 md:hidden"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Install Ariv</p>
          <p className="text-xs text-gray-600 leading-snug">Get the app on your home screen for faster access.</p>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Not now"
          className="text-gray-400 text-sm px-2 py-1"
        >
          Not now
        </button>
        <button
          onClick={handleInstall}
          className="bg-[#1F3A68] text-white text-sm font-semibold px-4 py-2 rounded-lg"
        >
          Install
        </button>
      </div>
    );
  }

  // iOS banner — Share + Add to Home Screen steps.
  if (iosBanner) {
    return (
      <div
        role="dialog"
        aria-label="Add Ariv to Home Screen"
        className="fixed left-3 right-3 bottom-3 z-50 bg-white border border-gray-200 shadow-xl rounded-2xl p-4 md:hidden"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-start gap-3">
          <img src="/icons/icon-192.png" alt="" className="w-12 h-12 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Add Ariv to your Home Screen</p>
            <p className="text-xs text-gray-600 leading-snug mt-1">
              Tap the Share button{" "}
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-700 text-xs align-middle"
              >
                ⬆
              </span>
              {" "}then choose <strong>Add to Home Screen</strong>.
            </p>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="text-gray-400 text-lg leading-none px-2"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return null;
}
